import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/a3c86433-81c0-4270-989b-c580f4239380.png" 
                alt="PINTOS LTDA Logo" 
                className="h-10 w-10 object-contain"
              />
              <h1 className="text-xl font-bold text-gray-900">PINTOS LTDA</h1>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('products')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
              <button onClick={() => scrollToSection('features')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20"></div>
        <div className="w-full px-8 py-24 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-3 space-y-8">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-light">iOS Development Experts</p>
                <h2 className="text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                  Modern Applications<br />
                  For iOS Platform<br />
                  <span className="text-teal-600">Enhanced Security</span>
                </h2>
                <p className="text-lg text-gray-600 max-w-xl leading-relaxed">
                  Specializing in VPN and Cleaner applications designed to enhance user experience and improve the security of mobile devices
                </p>
              </div>
              <div className="lg:col-span-2 relative">
                <div className="relative w-full h-96 flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 h-64 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 bg-gradient-to-br from-teal-400/30 to-blue-400/30 rounded-full blur-2xl animate-pulse"></div>
                  </div>
                  <img 
                    src="https://public.readdy.ai/ai/img_res/aeae59398c18066807e1cd861721996d.jpg" 
                    alt="iOS App Interface" 
                    className="relative z-10 w-64 h-auto object-contain drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-amber-50/30">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-1">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-8">About</p>
              <div className="text-8xl text-coral-400 font-serif leading-none">"</div>
            </div>
            <div className="lg:col-span-2 space-y-6">
              <h3 className="text-5xl font-bold text-gray-900 mb-8">PINTOS LTDA</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                A team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We combine cutting-edge technology with intuitive design to deliver applications that not only meet but exceed user expectations. Our commitment to quality and security ensures that every product we create provides real value to our users.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-16">
            <div className="lg:col-span-2 h-80 rounded-2xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20collaborating%20in%20modern%20bright%20office%20workspace%20with%20computers%20and%20monitors%20showing%20code%20warm%20natural%20lighting%20contemporary%20interior%20design%20teamwork%20atmosphere&width=600&height=400&seq=about-team-001&orientation=landscape" 
                alt="Team Collaboration" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="lg:col-span-3 h-80 rounded-2xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20minimalist%20workspace%20with%20MacBook%20and%20iPhone%20on%20clean%20white%20desk%20showing%20app%20development%20interface%20soft%20natural%20window%20light%20professional%20tech%20environment%20clean%20aesthetic&width=800&height=400&seq=about-workspace-001&orientation=landscape" 
                alt="Workspace" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-16 gap-8">
            <h3 className="text-6xl font-bold text-gray-900 leading-tight">
              Our<br />Products
            </h3>
            <p className="text-lg text-gray-600 max-w-md">
              Innovative iOS applications designed to protect your privacy and optimize your device performance
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* VPN Product Card */}
            <div className="group border border-gray-200 rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="bg-white p-12 flex items-center justify-center h-80">
                <div className="w-32 h-32 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full blur-xl opacity-30"></div>
                    <i className="ri-shield-check-line text-8xl bg-gradient-to-br from-blue-500 to-purple-600 bg-clip-text text-transparent relative z-10"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 border-t border-gray-100">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">VPN Application</h4>
                <p className="text-gray-600 leading-relaxed">
                  Secure your online presence with our advanced VPN solution. Protect your data, maintain privacy, and access content freely across the globe.
                </p>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="group border border-gray-200 rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="bg-white p-12 flex items-center justify-center h-80">
                <div className="w-32 h-32 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-teal-600 rounded-full blur-xl opacity-30"></div>
                    <i className="ri-brush-line text-8xl bg-gradient-to-br from-green-500 to-teal-600 bg-clip-text text-transparent relative z-10"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 border-t border-gray-100">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Cleaner Application</h4>
                <p className="text-gray-600 leading-relaxed">
                  Optimize your device performance with our intelligent cleaner. Remove junk files, free up storage, and keep your iOS device running smoothly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <h3 className="text-6xl font-bold text-gray-900 mb-16 leading-tight">
            Key<br />Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Advanced Security</h4>
              <p className="text-gray-600 leading-relaxed">
                Military-grade encryption and advanced security protocols to protect your data and ensure complete privacy across all your online activities.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-speed-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">High Performance</h4>
              <p className="text-gray-600 leading-relaxed">
                Optimized algorithms ensure fast processing speeds without compromising your device's battery life or overall performance.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-coral-400 to-coral-500 flex items-center justify-center mb-6">
                <i className="ri-user-smile-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">User-Friendly Interface</h4>
              <p className="text-gray-600 leading-relaxed">
                Intuitive design that makes complex features accessible to everyone. Simple navigation and clear controls for seamless user experience.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Regular Updates</h4>
              <p className="text-gray-600 leading-relaxed">
                Continuous improvements and new features delivered through regular updates to keep your apps at the cutting edge of technology.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-6">
                <i className="ri-customer-service-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">24/7 Support</h4>
              <p className="text-gray-600 leading-relaxed">
                Dedicated support team available around the clock to assist you with any questions or issues you may encounter.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Global Coverage</h4>
              <p className="text-gray-600 leading-relaxed">
                Access servers and services worldwide with extensive global coverage ensuring reliable connections wherever you are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1200 800">
            <path d="M0,400 Q300,350 600,400 T1200,400" stroke="currentColor" fill="none" strokeWidth="1"/>
            <path d="M0,420 Q300,370 600,420 T1200,420" stroke="currentColor" fill="none" strokeWidth="1"/>
            <circle cx="200" cy="200" r="3" fill="currentColor"/>
            <circle cx="500" cy="300" r="3" fill="currentColor"/>
            <circle cx="800" cy="250" r="3" fill="currentColor"/>
            <circle cx="1000" cy="400" r="3" fill="currentColor"/>
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
            <div>
              <h3 className="text-5xl font-bold mb-4">
                Connect With<br />Our Team
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="text-sm uppercase tracking-wider text-teal-300 mb-3">Address</p>
                <p className="text-white leading-relaxed">
                  Rua ALVARO MENDES 1237<br />
                  City TERESINA<br />
                  Zip 64000-060
                </p>
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider text-teal-300 mb-3">Country</p>
                <p className="text-white">Brazil</p>
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider text-teal-300 mb-3">Email</p>
                <p className="text-white">info@pintosltda.com</p>
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider text-teal-300 mb-3">Number</p>
                <p className="text-white">+1 9789868181</p>
              </div>
            </div>
          </div>
          <div className="border-t border-teal-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="text-sm text-teal-300">© 2025 PINTOS LTDA. All rights reserved.</p>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg border border-teal-700 flex items-center justify-center hover:bg-teal-800 transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-lg"></i>
                </div>
                <div className="w-10 h-10 rounded-lg border border-teal-700 flex items-center justify-center hover:bg-teal-800 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-lg"></i>
                </div>
                <div className="w-10 h-10 rounded-lg border border-teal-700 flex items-center justify-center hover:bg-teal-800 transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-lg"></i>
                </div>
                <div className="w-10 h-10 rounded-lg border border-teal-700 flex items-center justify-center hover:bg-teal-800 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </div>
              </div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-teal-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
